import { useCallback, useEffect, useMemo, useState } from "react";
import { OWNER_TIMEZONE, type MetricDay } from "@photography-booking/shared";
import {
  fetchInsights,
  type InsightsPayload,
  type InsightsRangeSelector,
  type InsightsTotals,
} from "../api/admin-client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";
import {
  InsightsChart,
  trafficSourceSeries,
  type InsightsChartSeries,
} from "./insights-chart.js";

interface InsightsAdminProps {
  token: string;
}

/** The single-click range choices, in the order they are offered (Req 13.3). */
const RANGE_CHOICES: readonly InsightsRangeSelector[] = [7, 30, 90, "ytd"];

/** The range the tab opens on (Req 13.2). */
const DEFAULT_RANGE: InsightsRangeSelector = 30;

/** Everyday wording for a range button. */
function rangeLabel(choice: InsightsRangeSelector): string {
  return choice === "ytd" ? "Year to date" : `Last ${choice} days`;
}

/** Everyday wording for each headline total (Req 13.4). */
const TOTAL_LABELS: readonly { key: keyof InsightsTotals; label: string }[] = [
  { key: "siteViews", label: "Times your site was opened" },
  { key: "uniqueVisitors", label: "People who visited" },
  { key: "bookingFormsStarted", label: "Started the booking form" },
  { key: "inquiriesSubmitted", label: "Booking requests sent" },
  { key: "inquiriesApproved", label: "Booking requests you approved" },
];

/** Formats a `YYYY-MM-DD` Metric_Day as a short, readable label. */
function formatDay(day: MetricDay): string {
  // Read the day at midday UTC so the label can never slide to a neighbouring
  // date, whatever timezone the Owner's browser reports.
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return day;
  }
  return date.toLocaleDateString(undefined, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  });
}

/**
 * Formats an instant in the Owner's own timezone, so "last activity" reads as
 * her wall clock rather than the browser's (Req 14.6).
 */
function formatInstant(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    timeZone: OWNER_TIMEZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * A section's range totals, shown above its trend chart so the Owner still sees
 * one number per metric as well as its shape over the range.
 */
function RangeTotals({
  totals,
}: {
  totals: readonly { label: string; value: number }[];
}): JSX.Element {
  return (
    <ul className="insights-totals">
      {totals.map((total) => (
        <li className="insights-totals__item" key={total.label}>
          <span className="insights-totals__value">{total.value}</span>
          <span className="insights-totals__label">{total.label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Insights view (Req 13.1–13.13).
 *
 * Read-only presentation of the engagement metrics returned by
 * `GET /admin/insights`. The visit metrics, the traffic sources and the funnel
 * are per-day trend charts over the payload's `days[]` (Req 13.12) — one
 * {@link InsightsChart} each, with that section's range totals kept above it —
 * rather than a bar chart paired with a table of the same range totals, which
 * said the same thing twice. Every chart's values stay available as real table
 * text inside the chart's own disclosure (Req 13.10, 13.13).
 *
 * "Photos people looked at most" is deliberately not a chart: it is a ranked
 * list of at most 8 entries whose values are text already, and a per-day series
 * per photo answers a question the Owner is not asking.
 *
 * The wording is deliberately everyday: the Owner reads this herself, and every
 * number is an undercount because privacy tools and ad blockers suppress some
 * events, which the caveat states outright rather than implying.
 */
export function InsightsAdmin({ token }: InsightsAdminProps): JSX.Element {
  const [range, setRange] = useState<InsightsRangeSelector>(DEFAULT_RANGE);
  const [payload, setPayload] = useState<InsightsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const { status, reportError, clearStatus } = useAdminStatus();

  const load = useCallback(async () => {
    clearStatus();
    setFailed(false);
    setLoading(true);
    const result = await fetchInsights(token, { days: range });
    setLoading(false);
    if (result.ok) {
      setPayload(result.data);
      return;
    }
    setFailed(true);
    reportError(result.message);
  }, [token, range, reportError, clearStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  /** The x axis every chart on the tab shares: the range's Metric_Days. */
  const chartDays = useMemo<MetricDay[]>(
    () => (payload ? payload.days.map((day) => day.date) : []),
    [payload],
  );

  /** "People who visited": site views and approximate unique visitors. */
  const visitSeries = useMemo<InsightsChartSeries[]>(
    () =>
      payload
        ? [
            {
              label: "Times opened",
              values: payload.days.map((day) => day.siteViews),
            },
            {
              label: "People",
              values: payload.days.map((day) => day.uniqueVisitors),
            },
          ]
        : [],
    [payload],
  );

  /**
   * "Where visitors came from": the busiest sources plus one aggregate of the
   * rest, chosen from the payload by `insights-chart.tsx` so the cap and its
   * tie-breaking live in one place.
   */
  const sourceSeries = useMemo<InsightsChartSeries[]>(
    () => (payload ? trafficSourceSeries(payload.days) : []),
    [payload],
  );

  /**
   * "From visit to booking": one series per Booking_Funnel stage, named by the
   * payload's own stage labels so the wording is not duplicated here.
   */
  const funnelSeries = useMemo<InsightsChartSeries[]>(
    () =>
      payload
        ? payload.funnel.stages.map((stage) => ({
            label: stage.label,
            values: payload.days.map((day) => day.funnel[stage.key] ?? 0),
          }))
        : [],
    [payload],
  );

  // An empty range is a real, expected answer for a new site, so it gets its
  // own plain-language message rather than a grid of zeroes (Req 13.8).
  const hasActivity =
    payload !== null &&
    (payload.days.some((day) => day.siteViews > 0 || day.uniqueVisitors > 0) ||
      payload.totals.bookingFormsStarted > 0 ||
      payload.totals.inquiriesSubmitted > 0 ||
      payload.totals.inquiriesApproved > 0 ||
      payload.topImages.length > 0);

  return (
    <AdminPanel
      title="Insights"
      description="How people are finding and using your site."
      status={status}
    >
      <div className="insights">
        <div className="insights__controls">
          <span className="insights__controls-label" id="insights-range-label">
            Show
          </span>
          <div
            className="insights__range"
            role="group"
            aria-labelledby="insights-range-label"
          >
            {RANGE_CHOICES.map((choice) => (
              <button
                key={choice}
                type="button"
                className={
                  choice === range
                    ? "admin-button admin-button--primary"
                    : "admin-button"
                }
                aria-pressed={choice === range}
                onClick={() => setRange(choice)}
              >
                {rangeLabel(choice)}
              </button>
            ))}
          </div>
          {payload ? (
            <span className="insights__range-value">
              {`${formatDay(payload.range.start)} to ${formatDay(
                payload.range.end,
              )}`}
            </span>
          ) : null}
        </div>

        {/* Permanent caveats: the numbers are approximate (Req 13.5) and a
            returning visitor counts once per day (Req 8.3). */}
        <p className="insights__caveat">
          These numbers are approximate. Some visits go uncounted because
          privacy tools and ad blockers stop the site from reporting them, so
          the real numbers are a little higher.
        </p>
        <p className="insights__caveat">
          Someone who comes back on a later day counts once for each day they
          visit.
        </p>

        {loading ? (
          <p className="insights__loading" role="status">
            {"Loading your numbers\u2026"}
          </p>
        ) : null}

        {failed && !loading ? (
          <div className="insights__error">
            <span>
              Your numbers could not be loaded just now. Nothing is broken on
              your site.
            </span>
            <button
              type="button"
              className="admin-button"
              onClick={() => void load()}
            >
              Try again
            </button>
          </div>
        ) : null}

        {payload ? (
          <>
            <div className="insights-health">
              {payload.health.measurementMayHaveStopped ? (
                <div className="insights-banner insights-banner--stopped">
                  <p className="insights-banner__title">
                    Visit measurement may have stopped
                  </p>
                  <p>
                    Nothing was recorded on either of the last two full days.
                    That can mean nobody visited, but it can also mean the
                    counting itself has stopped working.
                  </p>
                </div>
              ) : null}

              {payload.health.problems.length > 0 ? (
                <div className="insights-banner">
                  <p className="insights-banner__title">
                    Something went wrong while collecting your numbers
                  </p>
                  <ul className="insights-banner__list">
                    {payload.health.problems.slice(0, 3).map((problem) => (
                      <li
                        className="insights-banner__item"
                        key={problem.problemId}
                      >
                        {`${formatInstant(problem.recordedAt)} — ${problem.reason}`}
                      </li>
                    ))}
                  </ul>
                  <p>
                    Some numbers for those days may be lower than they should
                    be.
                  </p>
                </div>
              ) : null}

              <p className="insights-activity">
                {payload.health.lastEventAt === null
                  ? "No activity has been recorded yet."
                  : `Last activity recorded ${formatInstant(
                      payload.health.lastEventAt,
                    )} (New York time).`}
              </p>
            </div>

            {hasActivity ? (
              <>
                <section className="insights-section" aria-label="Your totals">
                  <h3 className="insights-section__title">
                    Your numbers at a glance
                  </h3>
                  <ul className="insights-totals">
                    {TOTAL_LABELS.map((total) => (
                      <li className="insights-totals__item" key={total.key}>
                        <span className="insights-totals__value">
                          {payload.totals[total.key]}
                        </span>
                        <span className="insights-totals__label">
                          {total.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section
                  className="insights-section"
                  aria-label="People who visited"
                >
                  <h3 className="insights-section__title">
                    People who visited, day by day
                  </h3>
                  <RangeTotals
                    totals={[
                      {
                        label: "Times your site was opened",
                        value: payload.totals.siteViews,
                      },
                      {
                        label: "People who visited",
                        value: payload.totals.uniqueVisitors,
                      },
                    ]}
                  />
                  <InsightsChart
                    days={chartDays}
                    series={visitSeries}
                    caption="Times your site was opened, and how many people that was, for each day"
                    formatDay={formatDay}
                  />
                </section>

                <section
                  className="insights-section"
                  aria-label="Where visitors came from"
                >
                  <h3 className="insights-section__title">
                    Where visitors came from
                  </h3>
                  {payload.trafficSources.length === 0 ? (
                    <p className="insights-section__hint">
                      Nothing recorded for these dates yet.
                    </p>
                  ) : (
                    <>
                      <RangeTotals
                        totals={payload.trafficSources.map((entry) => ({
                          label: entry.source,
                          value: entry.siteViews,
                        }))}
                      />
                      <InsightsChart
                        days={chartDays}
                        series={sourceSeries}
                        caption="Where each visit came from, for each day"
                        formatDay={formatDay}
                      />
                    </>
                  )}
                </section>

                {/* A ranked list, not a time series: the values are text
                    already, so this section needs no chart and no disclosure
                    (Req 10.2, 13.12). */}
                <section
                  className="insights-section"
                  aria-label="Photos people looked at most"
                >
                  <h3 className="insights-section__title">
                    Photos people looked at most
                  </h3>
                  {payload.topImages.length === 0 ? (
                    <p className="insights-section__hint">
                      No photos were opened in these dates.
                    </p>
                  ) : (
                    <div className="insights-table-wrap">
                      <table className="insights-table">
                        <caption>
                          How many times each photo was opened, most opened
                          first
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">Photo</th>
                            <th scope="col" className="insights-table__number">
                              Times opened
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.topImages.map((image) => (
                            <tr key={image.imageId}>
                              <th scope="row">
                                {image.caption ?? image.imageId}
                                {image.removed ? (
                                  <span className="insights-table__removed">
                                    {" "}
                                    (no longer in your portfolio)
                                  </span>
                                ) : null}
                              </th>
                              <td className="insights-table__number">
                                {image.opens}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <section
                  className="insights-section"
                  aria-label="From visit to booking"
                >
                  <h3 className="insights-section__title">
                    From visit to booking
                  </h3>
                  {payload.funnel.hasUnmeasuredVisits ? (
                    <p className="insights-section__hint">
                      A later step here shows more than the step before it.
                      Usually that is because visit measurement started more
                      recently than these bookings, so the earlier steps are
                      missing visits nobody was counting yet. Privacy tools and
                      ad blockers hide a few more.
                    </p>
                  ) : null}
                  <div className="insights-funnel">
                    <RangeTotals
                      totals={payload.funnel.stages.map((stage) => ({
                        label: stage.label,
                        value: stage.count,
                      }))}
                    />
                    <InsightsChart
                      days={chartDays}
                      series={funnelSeries}
                      caption="How many people reached each step, for each day"
                      formatDay={formatDay}
                    />
                    {payload.funnel.conversions.map((conversion) => {
                      const from = payload.funnel.stages.find(
                        (stage) => stage.key === conversion.from,
                      );
                      const to = payload.funnel.stages.find(
                        (stage) => stage.key === conversion.to,
                      );
                      return (
                        <p
                          className="insights-funnel__conversion"
                          key={`${conversion.from}-${conversion.to}`}
                        >
                          {`${conversion.percent}% of "${
                            from?.label ?? conversion.from
                          }" went on to "${to?.label ?? conversion.to}".`}
                        </p>
                      );
                    })}
                  </div>
                </section>
              </>
            ) : (
              <p className="insights__empty">
                No visits have been recorded for these dates yet. Once people
                start opening your site, their visits will show up here.
              </p>
            )}
          </>
        ) : null}
      </div>
    </AdminPanel>
  );
}
